import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Play, Calendar, ChevronRight, Radio, ShoppingCart } from 'lucide-react';
import { useDetail } from '../context/DetailContext';
import { useSubscription } from '../context/SubscriptionContext';
import { pushContinue, checkAccess, ppvCheckout } from '../api/services';
import useYouTubeProgress from '../hooks/useYouTubeProgress';
import Paywall from './Paywall';

const ShowDetailModal = () => {
  const { show, closeShow } = useDetail();
  const { isSubscribed } = useSubscription();
  const nav = useNavigate();
  const [activeSeason, setActiveSeason] = useState(0);
  // playing = { type: 'youtube' | 'mp4', src: string, episodeIdx, key }
  const [playing, setPlaying] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [access, setAccess] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    setActiveSeason(0);
    setPlaying(null);
    setAccess(null);
    if (show) {
      document.body.style.overflow = 'hidden';
      pushContinue({
        show_id: show.show_id,
        media_type: 'original',
        title: show.title,
        poster_path: null,
        backdrop_path: null,
      }).catch(() => {});
      // Check entitlement for PPV
      if (show.pay_per_view) {
        checkAccess(show.show_id).then(setAccess).catch(() => setAccess({ has_access: false, pay_per_view: true, ppv_price_cents: show.ppv_price_cents || 999 }));
      } else {
        setAccess({ has_access: true, pay_per_view: false });
      }
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [show]);

  const buy = async () => {
    if (!show) return;
    setBuying(true);
    try {
      const { url } = await ppvCheckout(show.show_id);
      window.location.href = url;
    } catch (e) {
      alert('Could not start checkout. Please try again.');
      setBuying(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { setPlaying(null); closeShow(); } };
    if (show) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, closeShow]);

  // Progress tracking for YouTube episodes
  useYouTubeProgress(
    playing?.type === 'youtube' && show ? `show-yt-${show.show_id}-${playing.episodeIdx ?? 't'}` : null,
    show && playing?.type === 'youtube' ? {
      show_id: show.show_id,
      media_type: 'original',
      title: show.title,
      youtube_id: playing.src,
    } : null,
    !!(playing?.type === 'youtube' && show),
  );

  if (!show) return null;
  const seasons = show.seasons || [];
  const season = seasons[activeSeason];

  const tryPlay = (ep, idx, isTrailer = false) => {
    const hasFullAccess = show.pay_per_view ? access?.has_access : (isSubscribed || access?.has_access);
    // Trailer is always free for everyone
    if (!isTrailer && !hasFullAccess) {
      if (show.pay_per_view) {
        // Scroll to buy button — handled by visible CTA
        return;
      }
      setPaywall(true);
      return;
    }
    if (ep.video_url) {
      setPlaying({ type: 'mp4', src: ep.video_url, episodeIdx: isTrailer ? 't' : idx, key: ep.video_url });
    } else if (ep.youtube_id) {
      setPlaying({ type: 'youtube', src: ep.youtube_id, episodeIdx: isTrailer ? 't' : idx, key: ep.youtube_id });
    }
  };

  const mp4Progress = (e) => {
    try {
      const v = e.currentTarget;
      if (!v.duration || v.duration < 5) return;
      pushContinue({
        show_id: show.show_id,
        media_type: 'original',
        title: show.title,
        progress: Math.max(0, Math.min(1, v.currentTime / v.duration)),
        duration_sec: Math.round(v.duration),
      }).catch(() => {});
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-10 overflow-y-auto bg-black/85 backdrop-blur-sm">
      <div className="modal-in relative w-full max-w-5xl bg-[#0e0e14] rounded-2xl overflow-hidden border border-white/10">
        <button onClick={closeShow} className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center"><X size={20} /></button>

        <div className="relative aspect-video w-full bg-black">
          {playing ? (
            playing.type === 'youtube' ? (
              <iframe
                id={`show-yt-${show.show_id}-${playing.episodeIdx ?? 't'}`}
                title="player"
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${playing.src}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1`}
                frameBorder="0"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video
                className="absolute inset-0 w-full h-full object-contain bg-black"
                src={playing.src}
                controls
                autoPlay
                onTimeUpdate={(e) => {
                  // Throttle — only every ~5s
                  const v = e.currentTarget;
                  if (!v._lastReport || v.currentTime - v._lastReport > 5) {
                    v._lastReport = v.currentTime;
                    mp4Progress(e);
                  }
                }}
                onEnded={mp4Progress}
              />
            )
          ) : (
            <>
              <img src={show.backdrop_url || show.poster_url} alt={show.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e14] via-[#0e0e14]/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8 md:p-10 max-w-2xl">
                {show.pay_per_view && (
                  <div className="mb-3 inline-flex items-center gap-2 px-3 py-1 rounded-md text-[11px] font-bold tracking-widest bg-amber-500 text-black">
                    {show.is_live ? (<><Radio size={12} /> LIVE PPV</>) : (<><ShoppingCart size={12} /> PAY-PER-VIEW</>)}
                  </div>
                )}
                <span className="px-2 py-1 rounded-md text-[10px] font-bold tracking-widest bg-[#E50914] text-white ml-0">BLUNT ORIGINAL</span>
                <h2 className="font-display text-4xl md:text-6xl text-white leading-none mt-3">{show.title}</h2>
                {show.creator_name && <div className="text-white/70 text-sm mt-2">By {show.creator_name}</div>}
                <div className="mt-5 flex flex-wrap gap-3">
                  {show.trailer_youtube_id && (
                    <button onClick={() => tryPlay({ youtube_id: show.trailer_youtube_id }, null, true)} className="tv-focusable inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-black font-semibold hover:bg-white/90">
                      <Play size={18} className="fill-black" /> Watch Trailer
                    </button>
                  )}
                  {show.pay_per_view && access && !access.has_access && (
                    <button
                      onClick={buy}
                      disabled={buying}
                      className="tv-focusable inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold disabled:opacity-60"
                    >
                      <ShoppingCart size={18} /> {buying ? 'Loading…' : `Buy for $${((access?.ppv_price_cents || show.ppv_price_cents || 999)/100).toFixed(2)}`}
                    </button>
                  )}
                  {show.pay_per_view && show.is_live && access && !access.has_access && (
                    <button data-testid="live-events-upsell-btn" onClick={() => nav('/subscribe')} className="tv-focusable inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-semibold">
                      Included with Live Events · $7.99/mo
                    </button>
                  )}
                  {(show.pay_per_view ? access?.has_access : (access?.has_access || isSubscribed)) && (
                    <span className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-sm">
                      ✓ You have access
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="p-8 md:p-10">
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
            {show.year && <span className="flex items-center gap-1"><Calendar size={14} /> {show.year}</span>}
            <span className="px-2 py-0.5 rounded border border-white/20 text-[10px] tracking-wider">HD</span>
            <span className="text-white/50">{show.media_type?.toUpperCase()}</span>
          </div>
          <p className="mt-3 text-white/85 leading-relaxed text-[15px]">{show.description}</p>
          {(show.genres || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {show.genres.map((g, i) => (
                <span key={i} className="px-3 py-1 rounded-full bg-white/8 border border-white/10 text-xs">{g}</span>
              ))}
            </div>
          )}

          {seasons.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Episodes</h3>
                <select value={activeSeason} onChange={(e) => setActiveSeason(Number(e.target.value))}
                  className="px-3 py-2 rounded-lg bg-white/8 border border-white/15 text-white text-sm">
                  {seasons.map((s, i) => (
                    <option key={i} value={i}>Season {s.season_number}{s.title ? ` — ${s.title}` : ''}</option>
                  ))}
                </select>
              </div>
              <div className="divide-y divide-white/5">
                {(season?.episodes || []).map((ep, i) => {
                  const thumb = ep.youtube_id
                    ? `https://img.youtube.com/vi/${ep.youtube_id}/hqdefault.jpg`
                    : null;
                  return (
                    <button key={i} onClick={() => tryPlay(ep, i)}
                      className="w-full flex items-center gap-4 py-4 text-left hover:bg-white/5 px-3 rounded-lg transition">
                      <div className="w-10 text-2xl font-bold text-white/40">{i + 1}</div>
                      <div className="w-44 aspect-video rounded-md bg-black overflow-hidden flex-shrink-0 relative">
                        {thumb ? (
                          <img src={thumb} alt={ep.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display='none'; }} />
                        ) : ep.video_url ? (
                          <div className="w-full h-full flex items-center justify-center text-white/70 text-xs bg-white/5">MP4</div>
                        ) : null}
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-medium">{ep.title || `Episode ${i + 1}`}</div>
                        {ep.description && <div className="text-white/60 text-sm mt-1 line-clamp-2">{ep.description}</div>}
                        <div className="text-white/40 text-xs mt-1">
                          {ep.duration_min ? `${ep.duration_min} min` : ''}
                          {ep.video_url ? ' • MP4' : ep.youtube_id ? ' • YouTube' : ''}
                        </div>
                      </div>
                      <ChevronRight className="text-white/40" />
                    </button>
                  );
                })}
                {(!season || season.episodes?.length === 0) && (
                  <div className="py-6 text-white/50 text-center">No episodes yet.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <Paywall open={paywall} onClose={() => setPaywall(false)} title="Subscribe to play episodes" />
    </div>
  );
};

export default ShowDetailModal;
